import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { t, useLanguage, toggleLanguage } from "../lib/i18n";
import { useSiteSettings } from "../lib/siteSettings";
import { Lightning, SignOut, ShieldStar, Translate, DiscordLogo, Coffee, Heart, User } from "@phosphor-icons/react";
import NotificationsBell from "./NotificationsBell";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [lang] = useLanguage();
  const { settings } = useSiteSettings();
  const isPlayer = user && user.role === "player";
  const isAdmin = user && user.role === "admin";
  const bg = settings?.background_url || "";
  const discord = settings?.discord_url || "";
  const bmc = settings?.buymecoffee_url || "";
  const sponsorUrl = settings?.sponsor_url || "";
  const sponsorName = settings?.sponsor_name || "";

  const navItems = [
    { to: "/", label: t.navHome },
    { to: "/tournaments", label: t.navTournaments },
    { to: "/host", label: t.navHost },
    { to: "/decks", label: t.navDecks },
    { to: "/players", label: t.navPlayers },
    { to: "/leaderboard", label: t.navLeaderboard },
    { to: "/creators", label: t.navCreators },
  ];

  return (
    <div
      className="min-h-screen flex flex-col bg-[#05050A] text-white relative"
      data-testid="site-root"
    >
      {bg && (
        <div
          aria-hidden="true"
          data-testid="site-background"
          className="pointer-events-none fixed inset-0 -z-10 opacity-30"
          style={{
            backgroundImage: `linear-gradient(rgba(5,5,10,0.65), rgba(5,5,10,0.85)), url("${bg}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          }}
        />
      )}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#05050A]/85 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <div className="relative">
              <Lightning size={24} weight="fill" className="text-[#FF0055]" />
              <div className="absolute inset-0 blur-md bg-[#FF0055]/40 -z-10" />
            </div>
            <span className="font-display font-black uppercase italic tracking-tight text-xl">
              BEY<span className="text-[#FF0055]">X</span>.PL
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-150 ${
                    isActive ? "text-[#00E5FF]" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {discord && (
              <a
                href={discord}
                target="_blank"
                rel="noreferrer"
                data-testid="discord-link"
                title="Discord"
                className="hidden sm:inline-flex items-center justify-center w-9 h-9 border border-white/15 text-white/70 hover:text-[#5865F2] hover:border-[#5865F2]/60 transition-colors"
              >
                <DiscordLogo size={16} weight="fill" />
              </a>
            )}
            {bmc && (
              <a
                href={bmc}
                target="_blank"
                rel="noreferrer"
                data-testid="bmc-link"
                title={t.support}
                className="hidden sm:inline-flex items-center justify-center w-9 h-9 border border-white/15 text-white/70 hover:text-[#FFDD00] hover:border-[#FFDD00]/60 transition-colors"
              >
                <Coffee size={16} weight="fill" />
              </a>
            )}
            {sponsorUrl && (
              <a
                href={sponsorUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="sponsor-link"
                title={sponsorName || "Twierdza Pasji"}
                className="hidden sm:inline-flex items-center justify-center w-9 h-9 border border-white/15 text-white/70 hover:text-[#E2FF00] hover:border-[#E2FF00]/60 transition-colors"
              >
                <Heart size={16} weight="fill" />
              </a>
            )}
            <button
              onClick={toggleLanguage}
              data-testid="lang-toggle"
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-white/15 text-[10px] font-bold uppercase tracking-[0.25em] text-white/70 hover:text-white hover:border-white/30 transition-colors"
              title={lang === "pl" ? "Switch to English" : "Przełącz na polski"}
            >
              <Translate size={12} weight="bold" />
              <span data-testid="lang-current">{lang === "pl" ? "PL" : "EN"}</span>
            </button>
            {isPlayer && <NotificationsBell />}
            {/* Iter 29 — mobile-only compact profile/admin icon buttons that
                stay visible in the header. The larger pill buttons below
                remain the primary action on sm+ screens. */}
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="mobile-admin-icon"
                title={t.navAdmin}
                aria-label={t.navAdmin}
                className="sm:hidden inline-flex items-center justify-center w-9 h-9 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors"
              >
                <ShieldStar size={16} weight="fill" />
              </Link>
            )}
            {isPlayer && (
              <Link
                to="/me"
                data-testid="mobile-my-profile-icon"
                title={t.navMyProfile}
                aria-label={t.navMyProfile}
                className="sm:hidden inline-flex items-center justify-center w-9 h-9 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors"
              >
                <User size={16} weight="fill" />
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="admin-link"
                className="hidden sm:flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors"
              >
                <ShieldStar size={14} weight="fill" /> {t.navAdmin}
              </Link>
            )}
            {isPlayer && (
              <Link
                to="/me"
                data-testid="my-profile-link"
                className="hidden sm:flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors"
              >
                {t.navMyProfile}
              </Link>
            )}
            {user && user !== false ? (
              <button
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="logout-btn"
                className="p-2 text-white/60 hover:text-white"
                title={t.logout}
              >
                <SignOut size={18} />
              </button>
            ) : user === false ? (
              <>
                <Link
                  to="/login"
                  data-testid="login-link"
                  className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-white/70 hover:text-white px-3 py-2"
                >
                  {t.navLogin}
                </Link>
                <Link
                  to="/register"
                  data-testid="register-link"
                  className="hidden sm:inline-block text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] bg-[#FF0055] hover:bg-[#FF3377] text-white px-3 py-2"
                >
                  {t.navRegister}
                </Link>
              </>
            ) : null}
          </div>
        </div>

        <nav className="md:hidden border-t border-white/10 px-4 py-2 flex items-center gap-1 overflow-x-auto">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] whitespace-nowrap ${
                  isActive ? "text-[#00E5FF]" : "text-white/60"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          {/* Iter 29 — quick jump to Profile / Admin from mobile since the
              desktop pill buttons are hidden below the sm breakpoint. */}
          {isPlayer && (
            <NavLink
              to="/me"
              data-testid="mobile-my-profile-link"
              className={({ isActive }) =>
                `ml-auto px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] whitespace-nowrap border ${
                  isActive
                    ? "text-[#00E5FF] border-[#00E5FF]/60 bg-[#00E5FF]/10"
                    : "text-[#00E5FF] border-[#00E5FF]/40"
                }`
              }
            >
              {t.navMyProfile}
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="mobile-admin-link"
              className={({ isActive }) =>
                `${isPlayer ? "" : "ml-auto"} px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] whitespace-nowrap border inline-flex items-center gap-1 ${
                  isActive
                    ? "text-[#00E5FF] border-[#00E5FF]/60 bg-[#00E5FF]/10"
                    : "text-[#00E5FF] border-[#00E5FF]/40"
                }`
              }
            >
              <ShieldStar size={12} weight="fill" /> {t.navAdmin}
            </NavLink>
          )}
        </nav>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-display font-black uppercase italic text-2xl">
              BEY<span className="text-[#FF0055]">X</span>.PL
            </div>
            <p className="text-sm text-white/50 mt-2 max-w-xs">
              {settings?.site_tagline || t.tagline}
            </p>
            {(discord || bmc || sponsorUrl) && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {discord && (
                  <a
                    href={discord}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-discord"
                    className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] border border-[#5865F2]/40 text-[#5865F2] hover:bg-[#5865F2]/10"
                  >
                    <DiscordLogo size={14} weight="fill" /> Discord
                  </a>
                )}
                {bmc && (
                  <a
                    href={bmc}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-bmc"
                    className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] border border-[#FFDD00]/40 text-[#FFDD00] hover:bg-[#FFDD00]/10"
                  >
                    <Coffee size={14} weight="fill" /> {t.support}
                  </a>
                )}
                {sponsorUrl && (
                  <a
                    href={sponsorUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-sponsor"
                    className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] border border-[#E2FF00]/50 text-[#E2FF00] hover:bg-[#E2FF00]/10"
                  >
                    <Heart size={14} weight="fill" /> {sponsorName || "Twierdza Pasji"}
                  </a>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-bold mb-3">
              {t.footerCompete}
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/tournaments">{t.navTournaments}</Link></li>
              <li><Link to="/leaderboard">{t.navLeaderboard}</Link></li>
              <li><Link to="/players">{t.navPlayers}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-bold mb-3">
              {t.footerBuild}
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/decks">{t.navDecks}</Link></li>
              <li><Link to="/host">{t.navHost}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/30 font-mono">
          © {new Date().getFullYear()} BEYX.PL · {t.footerLegal}
        </div>
      </footer>
    </div>
  );
}
